import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { JobService } from './service';
import { CompanyService } from '../company/service';
import { ErrorToast, SuccessToast } from '../../shared/toasters/toasters';
import DeleteModal from '../../Common/DeleteModal';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import { Switch } from '../../shared/components/Switch';

const JobList = () => {
    const { t } = useTranslation();
    const [list, setList] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [term, setTerm] = useState('');
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [jobToDelete, setJobToDelete] = useState<number | null>(null);
    const [companyList, setCompanyList] = useState<any[]>([]);
    const [selectedCompany, setSelectedCompany] = useState<number | null>(null);

    const [searchParams, setSearchParams] = useSearchParams();
    const tab = (searchParams.get('tab') as 'all' | 'featured' | 'notFeatured') || 'all';

    const navigate = useNavigate();

    const entriesPerPageOptions = [
        { label: '5', value: 5 },
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '30', value: 30 },
        { label: '40', value: 40 },
    ];

    const handleTermChange = (e: any) => {
        setTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleEntriesPerPageChange = (selectedOption: any) => {
        setEntriesPerPage(selectedOption ? selectedOption.value : 10);
        setCurrentPage(1);
    };

    const handleCompanyChange = (selectedOption: any) => {
        setSelectedCompany(selectedOption ? selectedOption.value : null);
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const handleDeleteClick = (id: number) => {
        setJobToDelete(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (jobToDelete !== null) {
            try {
                await JobService.delete(jobToDelete);
                SuccessToast(t('job.successDelete'));
                setShowDeleteModal(false);
                setJobToDelete(null);
                fetchData();
            } catch (error) {
                ErrorToast(error);
            }
        }
    };

    const handleTabSelect = (k: string | null) => {
        setSearchParams({ tab: k || 'all' });
    };

    const handleToggleFeatured = async (id: number, isFeatured: boolean) => {
        try {
            await JobService.updateFeatured(id, { featured: isFeatured });
            SuccessToast(t("job.featuredUpdated"));
            fetchData();
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchData = async () => {
        try {
            const response = await JobService.getAll({
                page: currentPage,
                perPage: entriesPerPage,
                term,
                company: selectedCompany,
                featured: tab === 'featured' ? true : tab === 'notFeatured' ? false : undefined,
            });

            const data = response.data;
            setList(data);
            setTotalPages(response.totalPages);
        } catch (error) {
            ErrorToast(error);
        }
    };

    const fetchCompanies = async () => {
        try {
            const response = await CompanyService.getAll({ perPage: 0, page: 1 });
            const companies = response.data.map((company: any) => ({
                label: company.name,
                value: company.id,
            }));
            setCompanyList(companies);
        } catch (error) {
            ErrorToast(error);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line
    }, [currentPage, entriesPerPage, term, selectedCompany, tab]);

    return (
        <React.Fragment>
            <Col>
                <Card>
                    <CardBody className="card-body">
                        <div className='text-end'>
                            <button className='btn btn-primary text-end' onClick={() => navigate('/jobs/create')}>
                                <i className='ti ti-plus f-18'></i> {t('job.addJob')}
                            </button>
                        </div>

                        <div className="mb-3">
                            <Tabs
                                id="job-list-tabs"
                                activeKey={tab}
                                onSelect={handleTabSelect}
                                className="mb-3"
                            >
                                <Tab eventKey="all" title={t('job.all')}>
                                    <JobTable
                                        list={list}
                                        t={t}
                                        navigate={navigate}
                                        handleDeleteClick={handleDeleteClick}
                                        handleToggleFeatured={handleToggleFeatured}
                                        handleTermChange={handleTermChange}
                                        handleEntriesPerPageChange={handleEntriesPerPageChange}
                                        entriesPerPageOptions={entriesPerPageOptions}
                                        entriesPerPage={entriesPerPage}
                                        term={term}
                                        companyList={companyList}
                                        handleCompanyChange={handleCompanyChange}
                                        selectedCompany={selectedCompany}
                                    />
                                </Tab>
                                <Tab eventKey="featured" title={t('job.featured')}>
                                    <JobTable
                                        list={list}
                                        t={t}
                                        navigate={navigate}
                                        handleDeleteClick={handleDeleteClick}
                                        handleToggleFeatured={handleToggleFeatured}
                                        handleTermChange={handleTermChange}
                                        handleEntriesPerPageChange={handleEntriesPerPageChange}
                                        entriesPerPageOptions={entriesPerPageOptions}
                                        entriesPerPage={entriesPerPage}
                                        term={term}
                                        companyList={companyList}
                                        handleCompanyChange={handleCompanyChange}
                                        selectedCompany={selectedCompany}
                                    />
                                </Tab>
                                {/* <Tab eventKey="notFeatured" title={t('job.featuredFalse')}>
                                    <JobTable
                                        list={list}
                                        t={t}
                                        navigate={navigate}
                                        handleDeleteClick={handleDeleteClick}
                                        handleToggleFeatured={handleToggleFeatured}
                                        handleTermChange={handleTermChange}
                                        handleEntriesPerPageChange={handleEntriesPerPageChange}
                                        entriesPerPageOptions={entriesPerPageOptions}
                                        entriesPerPage={entriesPerPage}
                                        term={term}
                                        companyList={companyList}
                                        handleCompanyChange={handleCompanyChange}
                                        selectedCompany={selectedCompany}
                                    />
                                </Tab> */}
                            </Tabs>
                        </div>

                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                          previousLabel={t('job.previous')}
                          nextLabel={t('job.next')}
                        />
                    </CardBody>
                </Card>
            </Col>

            <DeleteModal
                show={showDeleteModal}
                handleClose={() => setShowDeleteModal(false)}
                handleDeleteId={handleDeleteConfirm}
                name="job"
            />
        </React.Fragment>
    );
};

// JobTable component
import { Tabs, Tab } from 'react-bootstrap';
import Pagination from '../../Common/Pagination';

const JobTable = ({
    list,
    t,
    navigate,
    handleDeleteClick,
    handleToggleFeatured,
    handleTermChange,
    handleEntriesPerPageChange,
    entriesPerPageOptions,
    entriesPerPage,
    term,
    companyList,
    handleCompanyChange,
    selectedCompany,
}: any) => (
    <>
        <div className="d-sm-flex align-items-center mb-3">
            <ul className="list-inline me-auto my-1">
                <li className="list-inline-item">
                    <Select
                        placeholder={t('job.entriesPerPage')}
                        options={entriesPerPageOptions}
                        onChange={handleEntriesPerPageChange}
                        value={entriesPerPageOptions.find((option: any) => option.value === entriesPerPage)}
                        classNamePrefix="react-select"
                    />
                </li>
                <span> {t('job.entriesPerPage')}</span>
            </ul>
            <ul className="list-inline my-1 mt-3">
                <li className="list-inline-item">
                    <Select
                        placeholder={t('job.selectCompany')}
                        options={companyList}
                        onChange={handleCompanyChange}
                        value={companyList.find((c: any) => c.value === selectedCompany) || null}
                        isClearable
                        classNamePrefix="react-select"
                    />
                </li>
                <li className="list-inline-item">
                    <form className="">
                        <input
                            type="search"
                            className='form-control'
                            placeholder={t('job.searchPlaceholder')}
                            value={term}
                            onChange={handleTermChange}
                        />
                    </form>
                </li>
            </ul>
        </div>
        <div className="table-responsive">
            <table className="table table-hover sticky-actions" id="pc-dt-simple-1">
                <thead>
                    <tr>
                        <th>{t('job.id')}</th>
                        <th>{t('job.title')}</th>
                        <th>{t('job.company')}</th>
                        <th>{t('job.status')}</th>
                        <th>{t('job.applicationDeadline')}</th>
                        <th>{t('job.category')}</th>
                        <th>{t('job.locationType')}</th>
                        <th>{t('job.featured')}</th>
                        <th className="text-end sticky-col">{t('job.actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {list.map((item: any, index: number) => (
                        <tr key={index}>
                            <td>{item.id}</td>

                            <td className="wrap-col">{item.title}</td>
                            <td>{item.company?.name || item.companyName || item.company?.title || '-'}</td>
                            <td className='text-capitalize'>{item.status || (item.active ? 'active' : item.inactive ? 'inactive' : '-')}</td>
                            <td>{item.applicationDeadline ? new Date(item.applicationDeadline).toLocaleDateString() : '-'}</td>
                            <td>{item.category?.title}</td>
                            <td className={'text-capitalize'}>{item.locationType}</td>
                            <td className='text-center'>
                                <Switch
                                    className='form-check form-switch'
                                    id={`featured-${item.id}`}
                                    checked={item.featured}
                                    label=''
                                    onChange={(checked: boolean) => handleToggleFeatured(item.id, checked)}
                                />
                            </td>
                            <td className="text-end sticky-col">
                                <ul className="list-inline mb-0 text-nowrap">
                                    <li className="list-inline-item" onClick={() => navigate(`/jobs/edit/${item.id}`)}>
                                        <a href="#" className="avtar avtar-s btn-link-success btn-pc-default">
                                            <i className="ti ti-edit f-20"></i>
                                        </a>
                                    </li>
                                    <li className="list-inline-item">
                                        <span
                                            role="button"
                                            className="avtar avtar-s btn-link-danger btn-pc-default"
                                            onClick={() => handleDeleteClick(item.id)}
                                        >
                                            <i className="ti ti-trash f-20"></i>
                                        </span>
                                    </li>
                                </ul>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    </>
);

export default JobList;